import { Edit as EditIcon, LocationOn as LocationIcon, Person as PersonIcon, VisibilityOutlined as ViewIcon, MenuBookOutlined as WriteIcon } from '@mui/icons-material';
import Box from '@mui/material/Box';
import CircularProgress from '@mui/material/CircularProgress';
import IconButton from '@mui/material/IconButton';
import MenuItem from '@mui/material/MenuItem';
import Select from '@mui/material/Select';
import Stack from '@mui/material/Stack';
import { alpha, Theme } from '@mui/material/styles';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import { useNavigate } from '@tanstack/react-router';
import React, { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { AnnexButton } from './annex-button';

import { useNovel } from '@/context/novel-context';
import { useWriteScene } from '@/features/scenes/hooks/use-mutation-hooks';
import { useFirebase } from '@/firebase';
import { useSubscription } from '@/hooks/useSubscription';
import { EntitySummary, NarrativeSummary, TextSummary } from '@/lib/types';

function opaqueHoverBackground(theme: Theme) {
  if (theme.palette.mode === 'dark') return '#2C2C2C';
  if (theme.palette.background.paper.toUpperCase() === '#F4ECD8') return '#EBE2CE';
  return '#F6F5F4';
}




export interface SceneCardProps {
  scene: NarrativeSummary;
  characters: Record<string, EntitySummary>;
  locations: Record<string, EntitySummary>;
  organizations: Record<string, EntitySummary>;
  events: Record<string, EntitySummary>;
  contents: Record<string, TextSummary>;
  isDraggingOverlay?: boolean;
  isDragging?: boolean;
}

export function SceneCard({
  scene,
  characters,
  locations,
  organizations,
  events,
  contents,
  isDraggingOverlay,
  isDragging
}: SceneCardProps) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const povName = scene.povCharacterId ? (characters[scene.povCharacterId]?.name || t('common.unknown')) : t('common.unknown');
  const locationName = scene.locationIds?.[0] ? (locations[scene.locationIds[0]]?.name || t('common.unknown')) : t('common.unknown');

  // Display only events as tags
  const eventTags = (scene.eventIds || []).map((id: string) => events[id]?.name).filter(Boolean) as string[];

  // Define status: fallback to IDLE if missing
  const status = (scene.status?.phase || 'idle').toLowerCase();
  const isApproved = status === 'approved' || status === 'ready';

  const { hasFeature } = useSubscription();
  const isGenerativeAiAllowed = hasFeature('is_generative_ai_allowed');
  const [showAnnex, setShowAnnex] = useState(false);
  const hoverTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const { writeScene, isCreatingContent } = useWriteScene();
  const { backend } = useFirebase();
  const { novel } = useNovel();

  const handleUpdateStatus = async (phase: string) => {
    if (!novel.id || !scene) return;
    try {
      await backend.narrative.updateNarrativeItem(novel.ownerId, novel, 'scenes', scene.id, {
        status: { ...(scene.status || { phase: 'idle' }), phase }
      });
    } catch (error) {
      console.error('Failed to update status:', error);
    }
  };

  const handleMouseEnter = () => {
    if (isDraggingOverlay) return;
    hoverTimer.current = setTimeout(() => setShowAnnex(true), 1000);
  };

  const handleMouseLeave = () => {
    if (hoverTimer.current) clearTimeout(hoverTimer.current);
    setShowAnnex(false);
  };

  const handleWriteClick = async (e: React.MouseEvent) => {
    e.stopPropagation();
    e.preventDefault();
    await writeScene(scene);
  };

  return (
    <Box
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      sx={{
        position: 'relative',
        display: 'block',
      }}
    >
      <Box
        component="div"
        className="onboarding-scene-card"
        onClick={isDraggingOverlay ? undefined : handleWriteClick}
        sx={{
          display: 'block',
          bgcolor: 'background.paper',
          borderRadius: 3,
          border: '1px solid',
          borderColor: isDragging ? 'primary.main' : 'divider',
          transition: 'all 0.2s ease-in-out',
          cursor: isDragging ? 'grabbing' : 'pointer',
          textDecoration: 'none',
          position: 'relative',
          zIndex: 2, // Stay above the annex
          boxShadow: 2,
          '&:hover': {
            bgcolor: opaqueHoverBackground,
            borderColor: 'primary.main',
            ...(isDraggingOverlay ? {} : { transform: 'translateY(-2px)' }),
            boxShadow: 6,
          },
          userSelect: 'none',
          overflow: 'visible',
          minHeight: 240,
        }}
      >
        <Box sx={{ p: 2.5, display: 'flex', flexDirection: 'column', minHeight: 'inherit', position: 'relative' }}>
          <Tooltip title={t('common.actions.edit')}>
            <IconButton
              onClick={(e) => {
                e.stopPropagation();
                e.preventDefault();
                navigate({ to: '/scenes/edit/$id', params: { id: scene.id } });
              }}
              sx={{
                position: 'absolute',
                top: 14,
                right: 14,
                bgcolor: 'action.hover',
                color: 'text.secondary',
                zIndex: 10,
                width: 28,
                height: 28,
                '&:hover': {
                  bgcolor: 'primary.main',
                  color: 'primary.contrastText',
                },
              }}
              size="small"
            >
              <EditIcon sx={{ fontSize: 14 }} />
            </IconButton>
          </Tooltip>

          <Typography
            variant="h6"
            sx={{
              color: 'primary.main',
              mb: 1,
              fontWeight: 600,
              fontSize: '1.15rem',
              lineHeight: 1.2,
              pr: 4,
            }}
          >
            {scene.name}
          </Typography>

          <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 2, color: 'text.secondary' }}>
            <Stack direction="row" spacing={0.8} alignItems="center">
              <PersonIcon sx={{ fontSize: 18, opacity: 0.8 }} />
              <Typography variant="caption" sx={{ textTransform: 'uppercase', letterSpacing: '0.05em', fontWeight: 600 }}>
                {povName}
              </Typography>
            </Stack>
            <Stack direction="row" spacing={0.8} alignItems="center">
              <LocationIcon sx={{ fontSize: 18, opacity: 0.8 }} />
              <Typography variant="caption" sx={{ textTransform: 'uppercase', letterSpacing: '0.05em', fontWeight: 600 }}>
                {locationName}
              </Typography>
            </Stack>
          </Stack>

          <Stack spacing={1} sx={{ mb: 2 }}>
            {scene.goal && (
              <Typography variant="body2" sx={{ color: 'text.secondary', lineHeight: 1.5 }}>
                <Box component="span" sx={{ fontWeight: 600, color: 'text.primary', mr: 1 }}>{t('entity_form.scenes.goal_label')}:</Box>
                {scene.goal}
              </Typography>
            )}
            {scene.conflict && (
              <Typography variant="body2" sx={{ color: 'text.secondary', lineHeight: 1.5 }}>
                <Box component="span" sx={{ fontWeight: 600, color: 'text.primary', mr: 1 }}>{t('entity_form.scenes.conflict_label')}:</Box>
                {scene.conflict}
              </Typography>
            )}
            {!(scene.goal || scene.conflict) && scene.shortDescription && (
              <Typography variant="body2" sx={{ color: 'text.secondary', lineHeight: 1.6 }}>
                {scene.shortDescription}
              </Typography>
            )}
          </Stack>

          {eventTags.length > 0 && (
            <Stack direction="row" spacing={1} flexWrap="wrap" sx={{ mb: 2, gap: 1 }}>
              {eventTags.map((tag, idx) => (
                <Box key={idx} sx={{ bgcolor: 'action.hover', px: 1, py: 0.4, borderRadius: 1, border: '1px solid', borderColor: 'divider' }}>
                  <Typography variant="caption" sx={{ fontWeight: 700, fontSize: '0.7rem', color: 'text.secondary' }}>{tag}</Typography>
                </Box>
              ))}
            </Stack>
          )}

          <Box sx={{ mt: 'auto', pt: 2, borderTop: '1px solid', borderColor: 'divider', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 700, fontSize: '0.85rem' }}>
              {(scene.mainContentId && contents?.[scene.mainContentId]?.wordCount || 0).toLocaleString()} {t('editor.words')}
            </Typography>
            <Select
              value={status || 'idle'}
              onChange={(e) => handleUpdateStatus(e.target.value as string)}
              renderValue={(selected) => t(`writers_room.phases.${selected}`)}
              onClick={(e) => { e.stopPropagation(); e.preventDefault(); }}
              onOpen={() => {
                if (hoverTimer.current) clearTimeout(hoverTimer.current);
                setShowAnnex(false);
              }}
              size="small"
              variant="standard"
              disableUnderline
              sx={{
                height: 22,
                px: 1,
                borderRadius: 1,
                border: '1px solid',
                borderColor: (theme) => isApproved
                  ? alpha(theme.palette.success.main, 0.4)
                  : status === 'idle'
                    ? theme.palette.divider
                    : alpha(theme.palette.warning.main, 0.4),
                bgcolor: (theme) => isApproved
                  ? alpha(theme.palette.success.main, 0.1)
                  : status === 'idle'
                    ? theme.palette.action.hover
                    : alpha(theme.palette.warning.main, 0.1),
                color: isApproved ? 'success.main' : status === 'idle' ? 'text.secondary' : 'text.primary',
                '& .MuiSelect-select': {
                  py: 0,
                  px: 0.5,
                  fontSize: '0.65rem',
                  fontWeight: 700,
                  letterSpacing: '0.05em',
                  textTransform: 'uppercase',
                },
                '& .MuiSvgIcon-root': {
                  color: 'inherit',
                  fontSize: 14,
                }
              }}
            >
              <MenuItem value="idle"><Typography variant="caption" sx={{ fontWeight: 700, fontSize: 10, textTransform: 'uppercase' }}>{t('writers_room.phases.idle')}</Typography></MenuItem>
              {isGenerativeAiAllowed && <MenuItem value="snapshot"><Typography variant="caption" sx={{ fontWeight: 700, fontSize: 10, textTransform: 'uppercase' }}>{t('writers_room.phases.snapshot')}</Typography></MenuItem>}
              {isGenerativeAiAllowed && <MenuItem value="prepared"><Typography variant="caption" sx={{ fontWeight: 700, fontSize: 10, textTransform: 'uppercase' }}>{t('writers_room.phases.prepared')}</Typography></MenuItem>}
              <MenuItem value="writing"><Typography variant="caption" sx={{ fontWeight: 700, fontSize: 10, textTransform: 'uppercase' }}>{t('writers_room.phases.writing')}</Typography></MenuItem>
              <MenuItem value="editing"><Typography variant="caption" sx={{ fontWeight: 700, fontSize: 10, textTransform: 'uppercase' }}>{t('writers_room.phases.editing')}</Typography></MenuItem>
              {isGenerativeAiAllowed && <MenuItem value="validating"><Typography variant="caption" sx={{ fontWeight: 700, fontSize: 10, textTransform: 'uppercase' }}>{t('writers_room.phases.validating')}</Typography></MenuItem>}
              <MenuItem value="approved"><Typography variant="caption" sx={{ fontWeight: 700, fontSize: 10, textTransform: 'uppercase' }}>{t('writers_room.phases.approved')}</Typography></MenuItem>
              <MenuItem value="needs_fixes"><Typography variant="caption" sx={{ fontWeight: 700, fontSize: 10, textTransform: 'uppercase' }}>{t('writers_room.phases.needs_fixes')}</Typography></MenuItem>
              {isGenerativeAiAllowed && <MenuItem value="auto_fixing"><Typography variant="caption" sx={{ fontWeight: 700, fontSize: 10, textTransform: 'uppercase' }}>{t('writers_room.phases.auto_fixing')}</Typography></MenuItem>}
              <MenuItem value="ready"><Typography variant="caption" sx={{ fontWeight: 700, fontSize: 10, textTransform: 'uppercase' }}>{t('writers_room.phases.ready')}</Typography></MenuItem>
            </Select>
          </Box>
        </Box>
      </Box>

      {showAnnex && (
        <Box
          sx={{
            position: 'absolute',
            top: 0,
            right: -80,
            bottom: 0,
            width: 100,
            bgcolor: 'background.paper',
            borderRadius: '0 12px 12px 0',
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'space-between',
            alignItems: 'center',
            py: 3,
            pl: 2,
            zIndex: 1,
            boxShadow: 6,
            border: '1px solid',
            borderColor: 'divider',
            borderLeft: 'none',
            animation: 'slideIn 0.3s ease-out forwards',
            '@keyframes slideIn': {
              '0%': { opacity: 0, transform: 'translateX(-10px)' },
              '100%': { opacity: 1, transform: 'translateX(0)' },
            }
          }}
        >
          <Stack spacing={4} alignItems="center">
            <AnnexButton
              icon={<ViewIcon />}
              label={t('common.actions.view')}
              to={`/scenes/view/${scene.id}`}
            />
            <AnnexButton
              icon={<EditIcon />}
              label={t('common.actions.edit')}
              to={`/scenes/edit/${scene.id}`}
            />
          </Stack>

          <AnnexButton
            icon={isCreatingContent ? <CircularProgress size={20} color="inherit" /> : <WriteIcon />}
            label={t('common.actions.write')}
            onClick={handleWriteClick}
          />
        </Box>
      )}
    </Box>
  );
}
